import { loanOfficer } from '../clients';
import { API_ENDPOINTS } from '@/config/constants';
import { SINGLE_LOAN_OFFICER_API_BASE_URL } from '@/config/env';

/**
 * Loan Officer Address
 */
export interface LoanOfficerAddress {
  line: string;
  city: string;
  state: string;
  zipCode: string;
}

/**
 * Loan Officer entity from API
 */
export interface LoanOfficer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  nmlsId: string;
  photoUrl: string | null;
  address: LoanOfficerAddress;
  websiteUrl: string | null;
  landingPageUrl: string | null;
  title: string | null;
  bio: string | null;
  specialties: string[];
  languages: string[];
}

/**
 * Loan Officers API Response
 */
export interface LoanOfficersResponse {
  loanOfficers: LoanOfficer[];
  totalCount: number;
  pageSize: number;
}

/**
 * Query parameters for fetching loan officers
 */
export interface GetLoanOfficersParams {
  branchId?: string;
  state?: string;
  city?: string;
  zipCode?: string;
  limit?: number;
  offset?: number;
  fetchAll?: boolean;
}

/**
 * Loan Officer Service
 * Handles API calls related to loan officers
 */
export const loanOfficerService = {
  /**
   * Get loan officers with optional filtering and pagination
   * @param params - Query parameters for filtering and pagination
   * @returns Promise with loan officers response
   */
  async getLoanOfficers(
    params: GetLoanOfficersParams = {}
  ): Promise<LoanOfficersResponse> {
    const response = await loanOfficer.get<LoanOfficersResponse>(
      API_ENDPOINTS.LOAN_OFFICERS.BASE,
      {
        params: {
          branchId: params.branchId,
          state: params.state,
          city: params.city,
          zipCode: params.zipCode,
          limit: params.limit,
          offset: params.offset,
          fetchAll: params.fetchAll,
        },
      }
    );
    return response.data;
  },

  /**
   * Get a single loan officer by ID
   * @param id - Loan officer ID
   * @returns Promise with loan officer data
   */
  async getLoanOfficerById(id: string): Promise<LoanOfficer> {
    const response = await loanOfficer.get<LoanOfficer>(
      API_ENDPOINTS.LOAN_OFFICERS.BY_ID(id)
    );
    return response.data;
  },

  /**
   * Get a single loan officer by NMLS ID
   * Uses GET /api/v1/loan-officer?nmlsId=
   * @param params - Object containing nmlsId
   * @returns Promise with loan officer data
   */
  async getLoanOfficerByIdentifier(params: {
    nmlsId?: string;
  }): Promise<LoanOfficer> {
    // Use the singular loan-officer endpoint with nmlsId query param
    const response = await loanOfficer.get<LoanOfficer>('', {
      params: { nmlsId: params.nmlsId },
      baseURL: SINGLE_LOAN_OFFICER_API_BASE_URL,
    });
    return response.data;
  },
};

export default loanOfficerService;
